import { format } from 'date-fns';

import { SERVER_DATE_FORMAT } from '@nw/shared/config';

import { InstallmentOrderSnapshot } from '../types/productMachineTypes';

export type EnrollmentInstallmentMetaType = {
  installmentType: 'CUSTOMIZABLE' | 'EMI' | 'PERIODIC' | 'EXTERNAL_MANAGED';
  minimumAmountToBePaid: number;
  totalAmountToBePaid: number;
  defaultAmountToBePaid: number;
  appliedNoOfMonths?: number;
  appliedConfigId?: string;
  externalServiceType?: 'INSTITUTE';
  externalServiceReferenceId?: string;
} | null;

type EnrollmentInstallmentRequestFieldsType = {
  no_of_months?: number;
  periodic_installment_details?: { config_id?: string };
  customizable_installment_details?: {
    duration_to_pay_remaining_amount?: {
      duration_type: 'DAY' | 'MONTH';
      duration_value: number;
    };
    next_installment_datetime?: string;
  };
};

export const parseAmountValue = (value: unknown): number | null => {
  if (typeof value === 'number' && !Number.isNaN(value)) {
    return value;
  }
  if (typeof value === 'string' && value.trim() !== '') {
    const parsedValue = Number(value);
    if (!Number.isNaN(parsedValue)) {
      return parsedValue;
    }
  }
  return null;
};

export const getCustomizableInstallmentSnapshotByProductCode = (
  ordersDetails: any[],
  targetProductCode: string
): InstallmentOrderSnapshot | null => {
  const statusPriority: Record<string, number> = {
    IN_PROGRESS: 0,
    CREATED: 1,
    PAID: 2,
    CLOSED: 3
  };
  const matchedOrder = (ordersDetails ?? [])
    .filter((orderDetail) => {
      const installmentDetail = orderDetail?.installment_details;
      return (
        String(installmentDetail?.product_code ?? '').trim() ===
          String(targetProductCode).trim() &&
        installmentDetail?.installment_type === 'CUSTOMIZABLE'
      );
    })
    .sort((first, second) => {
      const firstStatus = String(
        first.installment_details?.installment_order_status ?? ''
      );
      const secondStatus = String(
        second.installment_details?.installment_order_status ?? ''
      );
      return (
        (statusPriority[firstStatus] ?? Number.MAX_SAFE_INTEGER) -
        (statusPriority[secondStatus] ?? Number.MAX_SAFE_INTEGER)
      );
    })[0];

  if (!matchedOrder) {
    return null;
  }

  const details =
    matchedOrder?.installment_details?.customizable_installment_details;
  const installmentOrderId = String(
    matchedOrder?.installment_details?.installment_order_id ?? ''
  ).trim();
  const amountPaid = Number(details?.amount_paid);
  const remainingAmountToBePaid = Number(details?.remaining_amount_to_be_paid);
  const minimumAmountToBePaidForInstallment = Number(
    details?.minimum_amount_to_be_paid_for_installment
  );

  return {
    installmentOrderId,
    amountPaid: Number.isNaN(amountPaid) ? 0 : Math.max(amountPaid, 0),
    remainingAmountToBePaid: Number.isNaN(remainingAmountToBePaid)
      ? null
      : Math.max(remainingAmountToBePaid, 0),
    minimumAmountToBePaidForInstallment: Number.isNaN(
      minimumAmountToBePaidForInstallment
    )
      ? null
      : Math.max(minimumAmountToBePaidForInstallment, 0)
  };
};

export const getEnrollmentInstallmentPaymentDetails = ({
  isEnrollmentFeeSection,
  enrollmentInstallmentMeta,
  values,
  fallbackAmount
}: {
  isEnrollmentFeeSection: boolean;
  enrollmentInstallmentMeta: EnrollmentInstallmentMetaType;
  values: Record<string, any>;
  fallbackAmount: number;
}): {
  amountToBePaid: number;
  paymentType: string;
  paidOrderRequestFields: EnrollmentInstallmentRequestFieldsType;
} => {
  if (!isEnrollmentFeeSection || !enrollmentInstallmentMeta) {
    return {
      amountToBePaid: fallbackAmount,
      paymentType: 'Full Payment',
      paidOrderRequestFields: {}
    };
  }

  if (enrollmentInstallmentMeta.installmentType === 'EXTERNAL_MANAGED') {
    return {
      amountToBePaid: 0,
      paymentType: 'Full Payment',
      paidOrderRequestFields: {}
    };
  }

  if (enrollmentInstallmentMeta.installmentType === 'CUSTOMIZABLE') {
    const selectedEnrollmentAmountToPayNow = parseAmountValue(
      values.enrollmentAmountToPayNow
    );
    const enrollmentRemainingAmountToBePaid = parseAmountValue(
      values.enrollmentRemainingAmountToBePaid
    );
    const enrollmentMinimumAmountToBePaid = parseAmountValue(
      values.enrollmentMinimumAmountToBePaidForInstallment ??
        enrollmentInstallmentMeta.minimumAmountToBePaid
    );
    const effectiveEnrollmentMinPayableNow =
      enrollmentRemainingAmountToBePaid != null
        ? Math.min(
            Math.max(enrollmentMinimumAmountToBePaid ?? 0, 100),
            enrollmentRemainingAmountToBePaid
          )
        : Math.max(enrollmentMinimumAmountToBePaid ?? 0, 100);
    const boundedEnrollmentAmountToPayNow =
      enrollmentRemainingAmountToBePaid != null
        ? Math.min(
            selectedEnrollmentAmountToPayNow ?? fallbackAmount,
            enrollmentRemainingAmountToBePaid
          )
        : selectedEnrollmentAmountToPayNow ?? fallbackAmount;
    const amountToBePaid = Math.max(
      boundedEnrollmentAmountToPayNow,
      effectiveEnrollmentMinPayableNow
    );
    const derivedEnrollmentPaymentType =
      enrollmentRemainingAmountToBePaid != null &&
      amountToBePaid < enrollmentRemainingAmountToBePaid
        ? 'Partial Payment'
        : 'Full Payment';
    const rawDurationType = String(
      values?.durationTypeForCustomizableInstallment ?? ''
    )
      .trim()
      .toUpperCase();
    const durationType =
      rawDurationType === 'DAY' || rawDurationType === 'MONTH'
        ? rawDurationType
        : null;
    const durationValue = parseAmountValue(
      values?.durationValueForCustomizableInstallment
    );
    const nextInstallmentDatetime = String(
      values?.nextInstallmentDateTimeForCustomizableInstallment ?? ''
    ).trim();
    const resolvedDurationType = durationType ?? 'MONTH';
    const resolvedDurationValue = durationValue ?? 2;
    const resolvedNextInstallmentDatetime =
      nextInstallmentDatetime ||
      format(
        new Date(new Date().setDate(new Date().getDate() + 1)),
        SERVER_DATE_FORMAT
      );

    return {
      amountToBePaid,
      paymentType:
        values?.enrollmentPaymentType || derivedEnrollmentPaymentType,
      paidOrderRequestFields: {
        customizable_installment_details: {
          duration_to_pay_remaining_amount: {
            duration_type: resolvedDurationType,
            duration_value: resolvedDurationValue
          },
          next_installment_datetime: resolvedNextInstallmentDatetime
        }
      }
    };
  }

  if (enrollmentInstallmentMeta.installmentType === 'EMI') {
    const appliedNoOfMonths = parseAmountValue(
      values.no_of_months ?? enrollmentInstallmentMeta.appliedNoOfMonths
    );
    return {
      amountToBePaid: fallbackAmount,
      paymentType: values?.enrollmentPaymentType || 'Full Payment',
      paidOrderRequestFields: {
        ...(appliedNoOfMonths != null
          ? { no_of_months: appliedNoOfMonths }
          : {})
      }
    };
  }

  const appliedConfigId = String(
    values.periodic_config_id ??
      values.config_id ??
      enrollmentInstallmentMeta.appliedConfigId ??
      ''
  ).trim();
  return {
    amountToBePaid: fallbackAmount,
    paymentType: values?.enrollmentPaymentType || 'Full Payment',
    paidOrderRequestFields: {
      ...(appliedConfigId
        ? { periodic_installment_details: { config_id: appliedConfigId } }
        : {})
    }
  };
};
